import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "antd";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import { DocumentsFilteringPanelV2 } from "../../../hoc/DocumentsFilteringPanelV2/DocumentsFilteringPanelV2";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { LocalFileStatus } from "../../../libs/coreapi-dto/dirs/globalImportInvoices";
import { IEntitySimpleDTO } from "../../../libs/coreapi-dto/@types/common";
import GridSelectorInput from "../../../components/controls/GridSelectorInput";
import { ContractorSelectorModal } from "../../Dictionaries/Contractor";
import InvoiceSettingsSelectorModal from "../../Dictionaries/Contractor/invoiceSettings/InvoiceSettings/InvoiceSettingsSelectorModal";
import { SettingsButton } from "../../../components/buttons/iconButtons/action/MainCollection";
import { StoreSelectorPanel } from "../../../components/storeSelectorPanel";
import styles from "./GlobalImportInvoicesFilteringPanel.module.scss";
import SearchIcon from "../../../components/comboBox/src/Icons/Search";
import { Delete2Button } from "../../../components/buttons/iconButtons/action/MainCollection";
import { RotationRightButton } from "../../../components/buttons/iconButtons/action/RotationCollection";
import BaseCommandsPanel from "../../../components/commandsPanels/BaseCommandsPanel";
import Tooltip from "../../../components/tooltips/Tooltip";

interface IGlobalImportInvoicesFilteringPanelProps {
    datePeriod: IDatePeriod;
    onDatePeriodChange: (value: IDatePeriod) => void;
    status?: LocalFileStatus | undefined;
    onStatusChange: (value: LocalFileStatus | undefined) => void;
    supplier: IEntitySimpleDTO | null;
    onSupplierChange: (value: IEntitySimpleDTO | null) => void;
    invoiceImportMappingModel: IEntitySimpleDTO | null;
    onInvoiceImportMappingModelChange: (value: IEntitySimpleDTO | null) => void;
    isInvoiceSettingEnabled: boolean;
    supplierImportProvider: string | null;
    contractor: IEntitySimpleDTO | null;
    onContractorChange: (value: IEntitySimpleDTO | null) => void;
    store: IEntitySimpleDTO | null;
    onStoreChange: (value: IEntitySimpleDTO | null) => void;
    contractorError?: string;
    storeError?: string;
    searchText?: string;
    onSearchChange: (value: string) => void;
    deleteProps: {
        onClick: () => void;
        disabled?: boolean;
    };
    refreshProps: {
        onClick: () => void;
        disabled?: boolean;
    };
}


export const GlobalImportInvoicesFilteringPanel: FC<IGlobalImportInvoicesFilteringPanelProps> = (props) => {
    const [localSearch, setLocalSearch] = useState<string>(props.searchText ?? "");
    const { t } = useTranslation();
    const tabsContext = useTabsContext();

    const statusOptions = [
        { value: null, label: t("documents.globalImportInvoices.status.all") },
        { value: LocalFileStatus.New, label: t("documents.globalImportInvoices.status.new") },
        { value: LocalFileStatus.Imported, label: t("documents.globalImportInvoices.status.imported") },
        { value: LocalFileStatus.Error, label: t("documents.globalImportInvoices.status.error") },
        { value: LocalFileStatus.Deleted, label: t("documents.globalImportInvoices.status.deleted") }
    ];

    useEffect(() => {
        const timer = setTimeout(() => {
            props.onSearchChange(localSearch);
        }, 400);
        return () => clearTimeout(timer);
    }, [localSearch]);

    useEffect(() => {
        if (props.searchText !== localSearch) {
            setLocalSearch(props.searchText ?? "");
        }
    }, [props.searchText]);

    return (
        <div className={styles.globalImportInvoicesFilteringPanel}>
            <div className={styles.topFilteringPanel}>
                <BaseCommandsPanel groups={[
                    [
                        {
                            iconButton: (
                                <Tooltip title="Удалить выбранные">
                                    <Delete2Button 
                                        sizeVariant="normal" 
                                        colorVariant="danger"
                                        disabled={props.deleteProps.disabled} 
                                        {...props.deleteProps} 
                                    />
                                </Tooltip>
                            )
                        },
                        {
                            iconButton: (
                                <Tooltip title="Обновить">
                                    <RotationRightButton 
                                        sizeVariant="normal"
                                        disabled={props.refreshProps.disabled} 
                                        {...props.refreshProps} 
                                    />
                                </Tooltip>
                            )
                        }
                    ]
                ]} />
                <DocumentsFilteringPanelV2
                    datePeriod={props.datePeriod}
                    onDatePeriodChange={props.onDatePeriodChange}
                    documentStatusType={props.status as any}
                    onDocumentStatusTypeChange={(value) => props.onStatusChange(value as unknown as LocalFileStatus)}
                    statusOptions={statusOptions}
                    statusPlaceholder={"Статус файла"}
                />
                <div className={styles.search}>
                     <Input
                        className="custom-input-withPrefix"
                        style={{ width: 280 }}
                        value={localSearch}
                        placeholder="Поиск"
                        prefix={<SearchIcon />}
                        onChange={(e) => setLocalSearch(e.currentTarget.value)}
                        allowClear
                    />
                </div>
            </div>
            <div className={styles.row}>
                <div className={styles.selectors}>
                    <GridSelectorInput
                        selectorModalJsx={ContractorSelectorModal}
                        className={styles.supplierFilter}
                        label="Поставщик"
                        placeholder="Поставщик"
                        inline
                        onSelect={(value) => props.onSupplierChange(value)}
                        onClear={() => props.onSupplierChange(null)}
                        selectedEntity={props.supplier ?? undefined}
                    />
                    <StoreSelectorPanel
                        required
                        contractor={{
                            className: styles.noMarginOnError,
                            label: "Аптека",
                            placeholder: "Аптека",
                            onSelect: (value) => props.onContractorChange(value),
                            onClear: () => {
                                props.onContractorChange(null);
                                props.onStoreChange(null);
                            },
                            treeViewCheckDirectoryType: "pharmacy",
                            error: props.contractorError
                        }}
                        store={{
                            className: styles.noMarginOnError,
                            label: "Склад",
                            placeholder: "Склад",
                            value: props.store,
                            onSelect: (value) => props.onStoreChange(value),
                            onClear: () => props.onStoreChange(null),
                            error: props.storeError
                        }}
                    />
                    
                </div>
                <div className={styles.invoiceSettingField}>
                    <GridSelectorInput
                        selectorModalJsx={InvoiceSettingsSelectorModal}
                        label={t("directory.contractors.settingInvoice")}
                        placeholder={t("directory.contractors.settingInvoice")}
                        inline
                        disabled={!props.isInvoiceSettingEnabled}
                        onSelect={(value) => props.onInvoiceImportMappingModelChange(value)}
                        onClear={() => props.onInvoiceImportMappingModelChange(null)}
                        selectedEntity={props.invoiceImportMappingModel ?? undefined}
                        data={{
                            extension: props.supplierImportProvider || undefined,
                            idContactor: props.supplier?.idGlobal,
                            name: props.supplier?.displayName
                        }}
                    />
                    <SettingsButton
                        sizeVariant="normal"
                        disabled={!props.isInvoiceSettingEnabled}
                        onClick={() => {
                            tabsContext.openChild(
                                "invoice_settings_creator",
                                !props.invoiceImportMappingModel ? "create" : "edit",
                                props.invoiceImportMappingModel?.idGlobal,
                                () => {},
                                {
                                    idContractor: props.supplier?.idGlobal,
                                    extension: props.supplierImportProvider || undefined,
                                    name: props.supplier?.displayName
                                }
                            );
                        }}
                    />
                </div>
                
            </div>
        </div>
    );
};

export default GlobalImportInvoicesFilteringPanel;
